import { useCallback } from "react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import { NewChip } from "components/NewTag/NewTag";
import type { IconButtonProps } from "@mui/material/IconButton";
import IconButton from "components/IconButton";
import XIcon from "components/Icons/XIcon";
import useOnboardingTooltip from "../hooks/useOnboardingTooltip";
import Typography from "components/Typography";
import type { TypographyProps } from "components/Typography";
import Anchor from "components/Anchor";
import type { AnchorProps } from "components/Anchor/Anchor";
import ArrowUpRightIcon from "components/Icons/ArrowUpRightIcon";

export const OnboardingTooltipTitleContent = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flex: "1 0 0",
}));

const OnboardingTooltipBadgeRoot = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
}));

export interface OnboardingTooltipBadgeProps extends Omit<ChipProps, "label"> {}

/**
 * @description Highlights the feature being introduced. Labelled "New" unless a label is given.
 */
export function OnboardingTooltipBadge(props: OnboardingTooltipBadgeProps) {
  const { t } = useTranslation();
  return (
    <OnboardingTooltipBadgeRoot>
      <NewChip label={t("global.new_label_text")} {...props} />
    </OnboardingTooltipBadgeRoot>
  );
}

const OnboardingTooltipCloseButtonRoot = styled(IconButton)(({ theme }) => ({
  marginLeft: "auto",
  /** ButtonBase clears the user agent outline and the theme only styles hover, so focus needs one. */
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "2px",
  },
}));

export interface OnboardingTooltipCloseButtonProps extends IconButtonProps {}

/**
 * @description Dismisses the tooltip through the `onClose` handler of the owning `<OnboardingTooltip />`.
 */
export function OnboardingTooltipCloseButton(props: OnboardingTooltipCloseButtonProps) {
  const { children, onClick, ...rest } = props;

  const { t } = useTranslation();
  const { onClose } = useOnboardingTooltip();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      onClose?.(event);
    },
    [onClick, onClose]
  );

  return (
    <OnboardingTooltipCloseButtonRoot
      size="small"
      color="primary"
      aria-label={t("global.close_label")}
      {...rest}
      onClick={handleClick}
    >
      {children || <XIcon fontSize="small" />}
    </OnboardingTooltipCloseButtonRoot>
  );
}

/**
 * @description Layout slot for everything rendered inside the tooltip bubble.
 * Stacks the header, description and any actions.
 */
export const OnboardingTooltipContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

const OnboardingTooltipDescriptionRoot = styled(Typography)({
  textAlign: "left",
});

export interface OnboardingTooltipDescriptionProps extends TypographyProps {}

/**
 * @description Explains what the feature does. Renders at the `TextS` scale unless a variant is given.
 * Describes the bubble it is rendered in.
 */
export function OnboardingTooltipDescription(props: OnboardingTooltipDescriptionProps) {
  const { descriptionId } = useOnboardingTooltip();

  return <OnboardingTooltipDescriptionRoot variant="TextS" id={descriptionId} {...props} />;
}

/**
 * @description Single row holding the badge, the title and the close button.
 * The close button pushes itself to the end of the row.
 */
export const OnboardingTooltipHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  alignSelf: "stretch",
  justifyContent: "space-between",
}));

const OnboardingTooltipLinkRoot = styled(Anchor)(({ theme }) => ({
  ...theme.typography.TextS,
  fontWeight: theme.typography.fontWeightMedium,
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

export interface OnboardingTooltipLinkProps extends Omit<AnchorProps, "children"> {}

/**
 * @description Sends the user to the help article for the feature.
 * Reads "Learn more" and opens in a new tab.
 */
export function OnboardingTooltipLink(props: OnboardingTooltipLinkProps) {
  const { t } = useTranslation();

  return (
    <OnboardingTooltipLinkRoot
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("global.learn_more_new_tab_label")}
      {...props}
    >
      {t("global.learn_more_label")}
      <ArrowUpRightIcon fontSize="small" />
    </OnboardingTooltipLinkRoot>
  );
}

const OnboardingTooltipTitleRoot = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

export interface OnboardingTooltipTitleProps extends TypographyProps {}

/**
 * @description The name of the feature being introduced.
 */
export function OnboardingTooltipTitle(props: OnboardingTooltipTitleProps) {
  const { titleId } = useOnboardingTooltip();

  return <OnboardingTooltipTitleRoot component="span" id={titleId} {...props} />;
}

export interface OnboardingTooltipBadgeTitleProps extends OnboardingTooltipTitleProps {}

/**
 * @description The name of the feature being introduced, preceded by the "New" badge.
 */
export function OnboardingTooltipBadgeTitle(props: OnboardingTooltipBadgeTitleProps) {
  return (
    <OnboardingTooltipTitleContent>
      <OnboardingTooltipBadge />
      <OnboardingTooltipTitle {...props} />
    </OnboardingTooltipTitleContent>
  );
}
